import type { CommercialInvoiceAnalysisResult } from "./types";

/** Row shape from `booking_commercial_invoice_analyses` (embedded or direct query). */
export interface CommercialInvoiceAnalysisCacheRow {
  booking_id?: number;
  object_path: string;
  file_name: string;
  likelihood_score: number;
  completeness_score: number;
  proforma_likelihood: number;
  document_type: CommercialInvoiceAnalysisResult["documentType"];
  result: Record<string, unknown>;
  analyzed_at: string;
}

export const normalizeCommercialInvoiceAnalysisEmbed = (
  value:
    | CommercialInvoiceAnalysisCacheRow
    | CommercialInvoiceAnalysisCacheRow[]
    | null
    | undefined
): CommercialInvoiceAnalysisCacheRow | null => {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
};

export const cacheRowToAnalysisResult = (
  row: CommercialInvoiceAnalysisCacheRow
): CommercialInvoiceAnalysisResult => {
  const payload = row.result as Partial<CommercialInvoiceAnalysisResult>;
  return {
    likelihoodScore: row.likelihood_score,
    proformaLikelihood: row.proforma_likelihood,
    completenessScore: row.completeness_score,
    documentType: row.document_type,
    requiredFields: payload.requiredFields ?? [],
    warnings: payload.warnings ?? [],
    signals: payload.signals ?? { proforma: [], commercial: [] },
    summary: payload.summary ?? "",
    extractionMethod: payload.extractionMethod ?? "limited",
    extractedTextPreview: payload.extractedTextPreview,
    deepCheck: payload.deepCheck,
    analyzedAt: row.analyzed_at,
    fileName: row.file_name,
  };
};

export const getCachedAnalysisForPath = (
  row: CommercialInvoiceAnalysisCacheRow | null | undefined,
  objectPath: string | null | undefined
): CommercialInvoiceAnalysisResult | null => {
  if (!row || !objectPath) return null;
  const normalizedPath = objectPath.trim().replace(/^\/+/, "");
  if (row.object_path !== normalizedPath) return null;
  return cacheRowToAnalysisResult(row);
};

export const commercialInvoiceValidityColor = (score: number): string => {
  if (score >= 75) return "#52c41a";
  if (score >= 50) return "#faad14";
  return "#ff4d4f";
};
